import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeContext } from "../../hooks/ThemeContext";
import { api } from "../../services/baseUrl";
import {
  ArrowRight,
  Star,
  CheckCircle2,
  Zap,
  Shield,
  Globe,
  ShoppingBag,
  RefreshCw,
  Sparkles,
  Quote,
  Award,
  ChevronRight,
} from "lucide-react";

const StarRow = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: n }).map((_, i) => (
      <span key={i} className="text-amber-400 text-sm leading-none">
        ★
      </span>
    ))}
  </div>
);

const SERVICES = [
  {
    Icon: Globe,
    title: "Site vitrine",
    tagline: "Votre vitrine digitale, sans compromis.",
    desc: "Un site qui capture l'essence de votre activite et donne envie de vous contacter des la premiere seconde. Design exclusif, zero template.",
    items: [
      "Design 100 % sur-mesure",
      "SEO optimise des le depart",
      "Responsive et ultra-rapide",
    ],
  },
  {
    Icon: ShoppingBag,
    title: "E-commerce",
    tagline: "Vendre en ligne, autrement.",
    desc: "Une boutique construite pour convertir - catalogue, paiement securise, experience d'achat irreprochable. Chaque detail compte.",
    items: [
      "UX pensee pour convertir",
      "Paiement securise",
      "Gestion catalogue intuitive",
    ],
  },
  {
    Icon: RefreshCw,
    title: "Refonte",
    tagline: "Votre site actuel, transforme.",
    desc: "Modernisez votre image en profondeur sans tout reconstruire - audit, redesign et migration soignee pour un resultat impressionnant.",
    items: [
      "Audit prealable complet",
      "Migration securisee",
      "Performance amelioree",
    ],
  },
];

const GARANTIES = [
  {
    Icon: CheckCircle2,
    titre: "Design exclusif",
    desc: "Aucun template. Chaque projet part de zero, concu pour vous.",
  },
  {
    Icon: Zap,
    titre: "Livraison rapide",
    desc: "Respect des delais, sans jamais sacrifier la qualite.",
  },
  {
    Icon: Shield,
    titre: "Suivi transparent",
    desc: "Espace client avec avancement visible en temps reel.",
  },
  {
    Icon: Award,
    titre: "Satisfaction garantie",
    desc: "Je ne lâche pas tant que vous n'êtes pas fiers du résultat.",
  },
];

const PROCESS = [
  {
    num: "01",
    titre: "Brief",
    desc: "J'echange sur vos objectifs, vos cibles et votre identite de marque. Appel discord ou formulaire, je m'adapte.",
  },
  {
    num: "02",
    titre: "Creation",
    desc: "Design, maquettes, developpement. Vous validez chaque etape. Rien n'avance sans votre accord.",
  },
  {
    num: "03",
    titre: "Lancement",
    desc: "Mise en ligne soignee, espace client, suivi post-livraison inclus. Je reste disponible apres.",
  },
];

const buildImageUrl = (path) => {
  if (!path) return null;
  if (String(path).startsWith("http")) return path;
  const base = (
    process.env.REACT_APP_API_URL || "https://api.krayneagency.fr/api"
  ).replace(/\/api\/?$/, "");
  let clean = String(path).replace(/^\/+/, "");
  if (clean.startsWith("storage/")) clean = clean.slice(8);
  return `${base}/api/images/${clean}`;
};

const Home = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [stats, setStats] = useState({ totalProducts: 0, recentRatings: [] });
  const [lastRealisation, setLastRealisation] = useState(null);

  useEffect(() => {
    let ok = true;
    api
      .get("/home/stats")
      .then((r) => {
        if (!ok) return;
        const d = r?.data || {};
        setStats({
          totalProducts: d.total_products || 0,
          recentRatings: d.recent_ratings || [],
        });
      })
      .catch(() => {});
    api
      .get("/realisations")
      .then((r) => {
        if (!ok) return;
        const list = r?.data?.data || r?.data || [];
        if (list.length > 0) {
          // Priorité à la réalisation "is_featured", sinon la plus récente
          const pick = list.find((x) => x.is_featured) || list[0];
          setLastRealisation(pick);
        }
      })
      .catch(() => {});
    return () => {
      ok = false;
    };
  }, []);

  const testimonials = useMemo(() => {
    const mapped = [...(stats.recentRatings || [])]
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((r) => ({
        author: r.user_name || "Client",
        initials: (r.user_name || "C")[0].toUpperCase(),
        text:
          r.comment?.length > 1
            ? r.comment
            : "Travail serieux et resultat au-dela des attentes.",
        rating: r.rating || 5,
        product: r.product_name || "Creation web",
      }));
    if (mapped.length) return mapped;
    return [
      {
        author: "Sophie L.",
        initials: "S",
        text: "Un rendu vraiment premium. Notre image a gagne en credibilite instantanement.",
        rating: 5,
        product: "Site vitrine",
      },
      {
        author: "Marc D.",
        initials: "M",
        text: "Le suivi est exemplaire. Je voyais l'avancement en direct sans avoir a relancer.",
        rating: 5,
        product: "Espace client",
      },
      {
        author: "Amina K.",
        initials: "A",
        text: "Mes clients me disent que mon site leur a donne confiance avant meme de m'appeler.",
        rating: 5,
        product: "Refonte",
      },
    ];
  }, [stats.recentRatings]);

  const bg = isDark ? "bg-[#07070a] text-white" : "bg-[#f9f9fb] text-gray-900";
  const sub = isDark ? "text-gray-400" : "text-gray-500";
  const cTxt = isDark ? "text-gray-300" : "text-gray-600";
  const card = isDark
    ? "bg-white/[0.04] border-white/[0.08] hover:border-white/[0.18]"
    : "bg-white border-gray-100 hover:border-gray-200 hover:shadow-2xl";
  const sep = isDark
    ? "bg-gradient-to-r from-transparent via-white/[0.10] to-transparent"
    : "bg-gradient-to-r from-transparent via-gray-200 to-transparent";

  const gPrimary = {
    backgroundImage:
      "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
  };
  const gWarm = isDark
    ? { backgroundImage: "linear-gradient(135deg, #fbbf24, #f59e0b)" }
    : { backgroundImage: "linear-gradient(135deg, #d97706, #b45309)" };

  const totalProjects = stats.totalProducts || 50;

  return (
    <div className={"flex-1 flex flex-col " + bg}>
      {/* ── 1. HERO ─────────────────────────────────────────────── */}
      <div
        className={
          "relative overflow-hidden " + (isDark ? "bg-[#07070a]" : "bg-white")
        }
      >
        {isDark && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />
        )}

        <div className="pointer-events-none absolute inset-0">
          <div
            className={
              "absolute -left-64 -top-64 h-[60rem] w-[60rem] rounded-full blur-[220px] " +
              (isDark ? "bg-violet-600/[0.13]" : "bg-violet-100/70")
            }
          />
          <div
            className={
              "absolute right-0 -top-20 h-[45rem] w-[45rem] rounded-full blur-[200px] " +
              (isDark ? "bg-rose-500/[0.10]" : "bg-rose-100/60")
            }
          />
          <div
            className={
              "absolute bottom-0 left-1/2 h-[28rem] w-[28rem] rounded-full blur-[180px] " +
              (isDark ? "bg-indigo-500/[0.07]" : "bg-indigo-50/80")
            }
          />
        </div>

        <div className="relative container mx-auto px-6 lg:px-10 pt-28 pb-40 lg:pt-36 lg:pb-48 max-w-7xl">
          <div className="grid gap-20 lg:grid-cols-[1.1fr_0.9fr] items-center">
            {/* Colonne gauche */}
            <div className="space-y-12">
              <div
                className={
                  "inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.25em] border " +
                  (isDark
                    ? "bg-white/[0.06] border-white/[0.10] text-gray-300"
                    : "bg-white border-gray-200 text-gray-500 shadow-sm")
                }
              >
                Studio Web & Digital Premium
              </div>

              <div className="space-y-7">
                <h1
                  className={
                    "text-5xl lg:text-6xl xl:text-[4.5rem] font-black leading-[1.06] " +
                    (isDark ? "text-white" : "text-gray-950")
                  }
                  style={{ letterSpacing: "-0.04em" }}
                >
                  Votre image en ligne
                  <br />
                  doit etre{" "}
                  <span
                    className="bg-clip-text text-transparent"
                    style={gPrimary}
                  >
                    inoubliable.
                  </span>
                </h1>
                <p
                  className={
                    "text-xl lg:text-2xl max-w-xl leading-relaxed font-light " +
                    sub
                  }
                >
                  Je conçois des sites qui parlent avant même que vos visiteurs
                  lisent le premier mot.{" "}
                  <span
                    className={
                      "font-semibold " +
                      (isDark ? "text-gray-200" : "text-gray-700")
                    }
                  >
                    100&nbsp;% sur-mesure, zero template.
                  </span>
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl font-bold text-base text-white shadow-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_60px_rgba(168,85,247,0.4)]"
                  style={{
                    background:
                      "linear-gradient(135deg, #f43f5e, #a855f7, #6366f1)",
                  }}
                >
                  Demarrer mon projet
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/portfolio"
                  className={
                    "inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-base border transition-all duration-300 hover:-translate-y-0.5 " +
                    (isDark
                      ? "border-white/[0.12] bg-white/[0.04] hover:bg-white/[0.08] text-white"
                      : "border-gray-200 bg-white hover:bg-gray-50 text-gray-800 shadow-sm")
                  }
                >
                  Voir le portfolio
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-2 w-fit">
                {[
                  { val: totalProjects + "+", label: "Projets livrés" },
                  { val: "98%", label: "Satisfaction client" },
                  { val: "< 24h", label: "Premier retour" },
                ].map(({ val, label }, i) => (
                  <div
                    key={label}
                    className={`flex flex-col gap-1 ${
                      i > 0
                        ? isDark
                          ? "border-l border-white/[0.10] pl-4"
                          : "border-l border-gray-200 pl-4"
                        : ""
                    }`}
                  >
                    <p
                      className="text-2xl font-black bg-clip-text text-transparent leading-none"
                      style={gPrimary}
                    >
                      {val}
                    </p>
                    <p
                      className={"text-xs font-medium whitespace-nowrap " + sub}
                    >
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Colonne droite — dernière réalisation dynamique */}
            <div className="hidden lg:flex flex-col gap-4">
              {lastRealisation ? (
                <>
                  {/* Carte principale */}
                  <div
                    className={
                      "rounded-3xl border overflow-hidden shadow-2xl " +
                      (isDark
                        ? "bg-white/[0.05] border-white/[0.10]"
                        : "bg-white border-gray-100 shadow-xl")
                    }
                  >
                    {/* Image */}
                    {buildImageUrl(lastRealisation.main_image) && (
                      <div className="h-44 overflow-hidden">
                        <img
                          src={buildImageUrl(lastRealisation.main_image)}
                          alt={lastRealisation.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}

                    <div className="p-7 space-y-5">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p
                            className={
                              "text-[10px] font-bold uppercase tracking-widest mb-1 " +
                              (isDark ? "text-gray-500" : "text-gray-400")
                            }
                          >
                            Dernière réalisation
                          </p>
                          <p
                            className={
                              "text-base font-black leading-snug " +
                              (isDark ? "text-white" : "text-gray-900")
                            }
                          >
                            {lastRealisation.title}
                          </p>
                        </div>
                        {lastRealisation.category && (
                          <span
                            className={
                              "shrink-0 rounded-full px-3 py-1 text-[11px] font-bold border " +
                              (isDark
                                ? "text-violet-300 bg-violet-500/10 border-violet-500/20"
                                : "text-violet-700 bg-violet-50 border-violet-200")
                            }
                          >
                            {typeof lastRealisation.category === "object"
                              ? lastRealisation.category?.name ||
                                lastRealisation.category?.name_category
                              : lastRealisation.category}
                          </span>
                        )}
                      </div>

                      {/* Description courte */}
                      {(lastRealisation.short_description ||
                        lastRealisation.description) && (
                        <p
                          className={
                            "text-sm leading-relaxed line-clamp-2 " + sub
                          }
                        >
                          {lastRealisation.short_description ||
                            lastRealisation.description}
                        </p>
                      )}

                      {/* Tech stack */}
                      {Array.isArray(lastRealisation.tech_stack) &&
                        lastRealisation.tech_stack.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {lastRealisation.tech_stack
                              .slice(0, 5)
                              .map((tech) => (
                                <span
                                  key={tech}
                                  className={
                                    "rounded-lg px-2.5 py-1 text-[11px] font-semibold " +
                                    (isDark
                                      ? "bg-white/[0.07] text-gray-300"
                                      : "bg-gray-100 text-gray-600")
                                  }
                                >
                                  {tech}
                                </span>
                              ))}
                          </div>
                        )}

                      {/* Client + lien */}
                      <div
                        className={
                          "flex items-center justify-between pt-2 border-t " +
                          (isDark ? "border-white/[0.07]" : "border-gray-100")
                        }
                      >
                        {lastRealisation.client_name && (
                          <p className={"text-xs " + sub}>
                            <span
                              className={
                                isDark ? "text-gray-500" : "text-gray-400"
                              }
                            >
                              Client —{" "}
                            </span>
                            <span className="font-semibold">
                              {lastRealisation.client_name}
                            </span>
                          </p>
                        )}
                        <Link
                          to={`/portfolio/${lastRealisation.id_realisation}`}
                          className="text-xs font-bold text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-colors"
                        >
                          Voir le projet
                          <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Badges sous la carte */}
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={
                        "rounded-2xl border px-5 py-4 " +
                        (isDark
                          ? "bg-[#0f0f14] border-white/[0.12]"
                          : "bg-white border-gray-100 shadow-lg")
                      }
                    >
                      <p
                        className={
                          "text-[10px] font-bold uppercase tracking-widest mb-1.5 " +
                          (isDark ? "text-gray-500" : "text-gray-400")
                        }
                      >
                        Design sur-mesure
                      </p>
                      <p
                        className="text-lg font-black bg-clip-text text-transparent"
                        style={gPrimary}
                      >
                        100 %
                      </p>
                    </div>
                    <div
                      className={
                        "rounded-2xl border px-5 py-4 " +
                        (isDark
                          ? "bg-[#0f0f14] border-white/[0.12]"
                          : "bg-white border-gray-100 shadow-lg")
                      }
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <p
                          className={
                            "text-sm font-bold " +
                            (isDark ? "text-white" : "text-gray-900")
                          }
                        >
                          Suivi en temps réel
                        </p>
                      </div>
                      <p className={"text-xs " + sub}>Espace client inclus</p>
                    </div>
                  </div>
                </>
              ) : (
                /* Squelette si aucune réalisation */
                <div
                  className={
                    "rounded-3xl border p-8 shadow-2xl space-y-5 animate-pulse " +
                    (isDark
                      ? "bg-white/[0.05] border-white/[0.10]"
                      : "bg-white border-gray-100")
                  }
                >
                  <div
                    className={
                      "h-4 w-1/3 rounded " +
                      (isDark ? "bg-white/10" : "bg-gray-200")
                    }
                  />
                  <div
                    className={
                      "h-6 w-2/3 rounded " +
                      (isDark ? "bg-white/10" : "bg-gray-200")
                    }
                  />
                  <div
                    className={
                      "h-3 w-full rounded " +
                      (isDark ? "bg-white/10" : "bg-gray-200")
                    }
                  />
                  <div
                    className={
                      "h-3 w-4/5 rounded " +
                      (isDark ? "bg-white/10" : "bg-gray-200")
                    }
                  />
                  <div className="flex gap-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={
                          "h-6 w-16 rounded-lg " +
                          (isDark ? "bg-white/10" : "bg-gray-200")
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={"h-[2px] w-full " + sep} />

      {/* ── 2. GARANTIES ────────────────────────────────────────── */}
      <div className={"py-24 " + (isDark ? "bg-[#0c0c12]" : "bg-[#f4f4f8]")}>
        <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {GARANTIES.map(({ Icon, titre, desc }) => (
              <div
                key={titre}
                className={
                  "rounded-2xl border p-8 flex flex-col gap-5 transition-all duration-300 " +
                  card
                }
              >
                <div
                  className={
                    "h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0 " +
                    (isDark
                      ? "bg-violet-500/[0.15] border border-violet-500/20"
                      : "bg-violet-50 border border-violet-100")
                  }
                >
                  <Icon className="w-5 h-5 text-violet-500" />
                </div>
                <div>
                  <p
                    className={
                      "font-bold text-base mb-2 " +
                      (isDark ? "text-white" : "text-gray-900")
                    }
                  >
                    {titre}
                  </p>
                  <p className={"text-sm leading-relaxed " + sub}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={"h-[2px] w-full " + sep} />

      {/* ── 3. SERVICES ─────────────────────────────────────────── */}
      <div
        className={"py-32 lg:py-40 " + (isDark ? "bg-[#07070a]" : "bg-white")}
      >
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <div className="text-center mb-20 space-y-6">
            <p
              className={
                "text-xs font-bold uppercase tracking-[0.3em] " +
                (isDark ? "text-violet-400" : "text-violet-600")
              }
            >
              Ce que je propose
            </p>
            <h2
              className={
                "text-4xl lg:text-5xl font-black " +
                (isDark ? "text-white" : "text-gray-950")
              }
              style={{ letterSpacing: "-0.035em" }}
            >
              Trois expertises,{" "}
              <span className="bg-clip-text text-transparent" style={gPrimary}>
                un seul niveau.
              </span>
            </h2>
            <p
              className={
                "max-w-xl mx-auto text-lg lg:text-xl mb-3 leading-relaxed " +
                sub
              }
            >
              Votre futur client doit rester, explorer, puis vous contacter. Je
              conçois pour ça.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {SERVICES.map(({ Icon, title, tagline, desc, items }, idx) => (
              <div
                key={title}
                className={
                  "group relative rounded-3xl border p-8 lg:p-10 flex flex-col gap-5 transition-all duration-300 hover:-translate-y-1 " +
                  card
                }
              >
                <div
                  className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(244,63,94,0.05), rgba(168,85,247,0.05), rgba(99,102,241,0.05))",
                  }}
                />

                <div className="relative flex items-start justify-between">
                  <div
                    className="h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(244,63,94,0.15), rgba(168,85,247,0.12), rgba(99,102,241,0.10))",
                    }}
                  >
                    <Icon
                      className={
                        "w-7 h-7 " +
                        (isDark ? "text-violet-400" : "text-violet-600")
                      }
                    />
                  </div>
                  {idx === 1 && (
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-black text-white"
                      style={{
                        background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                      }}
                    >
                      Populaire
                    </span>
                  )}
                </div>

                <div className="relative space-y-3">
                  <h3
                    className={
                      "text-2xl font-black " +
                      (isDark ? "text-white" : "text-gray-950")
                    }
                  >
                    {title}
                  </h3>
                  <p
                    className={
                      "text-sm font-bold " +
                      (isDark ? "text-violet-400" : "text-violet-600")
                    }
                  >
                    {tagline}
                  </p>
                  <p
                    className={
                      "text-base leading-relaxed pt-1 line-clamp-4 " + cTxt
                    }
                  >
                    {desc}
                  </p>
                </div>

                <ul className="relative space-y-3.5 flex-1">
                  {items.map((it) => (
                    <li key={it} className="flex items-center gap-3">
                      <CheckCircle2
                        className={
                          "w-4 h-4 flex-shrink-0 " +
                          (isDark ? "text-emerald-400" : "text-emerald-500")
                        }
                      />
                      <span className={"text-sm font-medium " + cTxt}>
                        {it}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={
                    "relative inline-flex items-center gap-2 text-sm font-black transition-colors " +
                    (isDark
                      ? "text-violet-400 hover:text-violet-300"
                      : "text-violet-600 hover:text-violet-700")
                  }
                >
                  Demander un devis <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/services"
              className={
                "inline-flex items-center gap-2 text-sm font-semibold transition-colors " +
                (isDark
                  ? "text-gray-500 hover:text-white"
                  : "text-gray-400 hover:text-gray-800")
              }
            >
              Voir toutes nos prestations <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className={"h-[2px] w-full " + sep} />

      {/* ── 4. PROCESS ──────────────────────────────────────────── */}
      <div
        className={
          "py-32 lg:py-40 " + (isDark ? "bg-[#0c0c12]" : "bg-[#f4f4f8]")
        }
      >
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="text-center mb-20 space-y-6">
            <p
              className={
                "text-xs font-bold uppercase tracking-[0.3em] " +
                (isDark ? "text-violet-400" : "text-violet-600")
              }
            >
              Comment ca marche
            </p>
            <h2
              className={
                "text-4xl lg:text-5xl font-black " +
                (isDark ? "text-white" : "text-gray-950")
              }
              style={{ letterSpacing: "-0.035em" }}
            >
              Simple. Clair.{" "}
              <span className="bg-clip-text text-transparent" style={gPrimary}>
                Sans surprise.
              </span>
            </h2>
          </div>

          <div className="relative">
            <div
              className={
                "hidden md:block absolute top-10 h-px " +
                (isDark ? "bg-white/[0.08]" : "bg-gray-200")
              }
              style={{
                left: "calc(16.66% + 2.5rem)",
                right: "calc(16.66% + 2.5rem)",
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-14 lg:gap-20">
              {PROCESS.map(({ num, titre, desc }) => (
                <div
                  key={num}
                  className="flex flex-col items-center text-center gap-7"
                >
                  <div
                    className="relative z-10 flex h-24 w-24 items-center justify-center rounded-full border-2 shadow-2xl"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(244,63,94,0.12), rgba(168,85,247,0.12), rgba(99,102,241,0.10))",
                      borderColor: isDark
                        ? "rgba(168,85,247,0.35)"
                        : "rgba(168,85,247,0.30)",
                    }}
                  >
                    <span
                      className="text-2xl font-black bg-clip-text text-transparent"
                      style={gPrimary}
                    >
                      {num}
                    </span>
                  </div>
                  <div>
                    <p
                      className={
                        "text-xl font-black mb-3 " +
                        (isDark ? "text-white" : "text-gray-900")
                      }
                    >
                      {titre}
                    </p>
                    <p
                      className={
                        "text-base leading-relaxed max-w-[210px] mx-auto " + sub
                      }
                    >
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={"h-[2px] w-full " + sep} />

      {/* ── 5. TEMOIGNAGES ──────────────────────────────────────── */}
      <div
        className={"py-32 lg:py-40 " + (isDark ? "bg-[#07070a]" : "bg-white")}
      >
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-20 gap-6">
            <div className="space-y-4">
              <p
                className={
                  "text-xs font-bold uppercase tracking-[0.3em] " +
                  (isDark ? "text-violet-400" : "text-violet-600")
                }
              >
                Ils me font confiance
              </p>
              <h2
                className={
                  "text-4xl lg:text-5xl font-black " +
                  (isDark ? "text-white" : "text-gray-950")
                }
                style={{ letterSpacing: "-0.035em" }}
              >
                Ce qu'ils disent de moi.
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <StarRow n={5} />
              <span className={"text-base font-semibold " + sub}>4.9 / 5</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={t.author + i}
                className={
                  "rounded-3xl p-10 flex flex-col gap-7 " +
                  (isDark ? "bg-white/[0.04]" : "bg-gray-50")
                }
              >
                <Quote
                  className={
                    "w-9 h-9 " +
                    (isDark ? "text-violet-500/40" : "text-violet-200")
                  }
                />
                <p className={"text-base leading-relaxed flex-1 " + cTxt}>
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="space-y-5">
                  <StarRow n={t.rating} />
                  <div
                    className="flex items-center gap-4 pt-5"
                    style={{
                      borderTop: isDark
                        ? "1px solid rgba(255,255,255,0.07)"
                        : "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      className="h-11 w-11 rounded-full flex items-center justify-center font-black text-white text-sm flex-shrink-0"
                      style={{
                        background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                      }}
                    >
                      {t.initials}
                    </div>
                    <div>
                      <p
                        className={
                          "font-bold " +
                          (isDark ? "text-white" : "text-gray-900")
                        }
                      >
                        {t.author}
                      </p>
                      <p className={"text-sm mt-0.5 " + sub}>{t.product}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={"h-[2px] w-full " + sep} />

      {/* ── 6. CTA FINAL ────────────────────────────────────────── */}
      <div
        className={
          "relative overflow-hidden py-32 lg:py-40 " +
          (isDark ? "bg-[#0c0c12]" : "bg-[#f4f4f8]")
        }
      >
        <div className="pointer-events-none absolute inset-0">
          <div
            className={
              "absolute left-1/2 -translate-x-1/2 -top-32 h-[36rem] w-[36rem] rounded-full blur-[160px] " +
              (isDark ? "bg-violet-600/[0.18]" : "bg-violet-100")
            }
          />
          <div
            className={
              "absolute right-0 bottom-0 h-64 w-64 rounded-full blur-[120px] " +
              (isDark ? "bg-rose-500/[0.14]" : "bg-rose-100")
            }
          />
        </div>
        <div className="relative container mx-auto px-6 lg:px-10 max-w-4xl text-center">
          <div className="space-y-8">
            <p
              className={
                "text-xs font-bold uppercase tracking-[0.3em] " +
                (isDark ? "text-violet-400" : "text-violet-600")
              }
            >
              Pret a commencer ?
            </p>
            <h2
              className={
                "text-4xl lg:text-5xl font-black " +
                (isDark ? "text-white" : "text-gray-950")
              }
              style={{ letterSpacing: "-0.04em" }}
            >
              Votre futur client vous juge
              <br />
              <span className="bg-clip-text text-transparent" style={gPrimary}>
                en moins de 3 secondes.
              </span>
            </h2>
            <p className={"max-w-md mx-auto text-xl leading-relaxed " + sub}>
              Je vous livre un site dont vous serez fier. Devis gratuit, reponse
              sous 24h.
            </p>
            <div className="flex flex-wrap justify-center gap-5 pt-2">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl font-black text-lg text-white shadow-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_24px_80px_rgba(168,85,247,0.45)]"
                style={{
                  background:
                    "linear-gradient(135deg, #f43f5e, #a855f7, #6366f1)",
                }}
              >
                Demarrer maintenant
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className={
                  "inline-flex items-center gap-2 px-10 py-5 rounded-2xl font-semibold text-lg border transition-all duration-300 hover:-translate-y-0.5 " +
                  (isDark
                    ? "border-white/[0.12] bg-white/[0.04] hover:bg-white/[0.08] text-white"
                    : "border-gray-200 bg-white hover:bg-gray-50 text-gray-800 shadow-sm")
                }
              >
                Voir le portfolio
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
